import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import SearchBar from '../components/UI/SearchBar';
import api from '../config/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import Modal from '../components/UI/Modal';
import ClientForm from '../components/Clients/ClientForm';
// ClientTable removed - rendering table inline in this page now
import toast from 'react-hot-toast';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchClients();
    fetchStats();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients');
      setClients(response.data.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/clients/stats/summary');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching client stats:', error);
    }
  };

  const handleAddClient = () => {
    setEditingClient(null);
    setIsModalOpen(true);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setIsModalOpen(true);
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await api.delete(`/clients/${clientId}`);
        toast.success('Client deleted successfully');
        fetchClients();
        fetchStats();
      } catch (error) {
        console.error('Error deleting client:', error);
        toast.error('Failed to delete client');
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const handleFormSubmit = () => {
    handleModalClose();
    fetchClients();
    fetchStats();
  };

  // Filter clients based on search and status
  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.phone.includes(searchQuery) ||
      client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.vehicleNumber.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = (() => {
      if (filterStatus === 'all') return true;
      
      const renewalDate = new Date(client.renewalDate);
      const today = new Date();
      const daysUntilRenewal = Math.ceil((renewalDate - today) / (1000 * 60 * 60 * 24));

      switch (filterStatus) {
        case 'active':
          return daysUntilRenewal > 30;
        case 'expiring':
          return daysUntilRenewal > 0 && daysUntilRenewal <= 30;
        case 'expired':
          return daysUntilRenewal <= 0;
        default:
          return true;
      }
    })();

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="page-header-title">Clients</h1>
            <p className="page-header-subtitle">Manage clients and their insurance policies</p>
          </div>
          <button onClick={handleAddClient} className="btn btn-primary mt-4 sm:mt-0">
            <Plus className="h-4 w-4 mr-2" />
            Add New Client
          </button>
        </div>
      </div>

      {/* Quick Metrics */}
      {stats && (
        <div className="metrics-bar">
          <div className="metrics-grid">
            <div className="metric-item">
              <span className="metric-label">Total Clients</span>
              <span className="metric-value">{stats.totalClients}</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Active Policies</span>
              <span className="metric-value">{stats.activePolicies}</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Expiring Soon</span>
              <span className="metric-value text-amber-600">{stats.expiringSoon}</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Expired</span>
              <span className="metric-value text-red-600">{stats.expired}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="filters-bar">
        <div className="filter-group">
          <div className="filter-input-group">
            <SearchBar
              value={searchQuery}
              onChange={(v) => setSearchQuery(v)}
              placeholder="Search by name, email, phone, or vehicle..."
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'active', 'expiring', 'expired'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`filter-chip ${filterStatus === status ? 'active' : ''}`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Clients Table */}
      <div className="flex-1 overflow-x-auto">
        {filteredClients.length > 0 ? (
          <div className="border-t border-gray-200">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Vehicle</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Renewal Date</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => {
                  const renewalDate = new Date(client.renewalDate);
                  const today = new Date();
                  const daysUntilRenewal = Math.ceil((renewalDate - today) / (1000 * 60 * 60 * 24));
                  
                  let statusLabel = 'Active';
                  let statusClass = 'status-active';
                  
                  if (daysUntilRenewal <= 0) {
                    statusLabel = 'Expired';
                    statusClass = 'status-expired';
                  } else if (daysUntilRenewal <= 30) {
                    statusLabel = 'Expiring Soon';
                    statusClass = 'status-expiring';
                  }
                  
                  return (
                    <tr key={client.id}>
                      <td className="font-medium">{client.fullName}</td>
                      <td>{client.vehicleNumber}</td>
                      <td className="text-gray-600">{client.email || '—'}</td>
                      <td className="text-gray-600">{client.phone}</td>
                      <td className="text-gray-600">{new Date(client.renewalDate).toLocaleDateString()}</td>
                      <td>
                        <span className={`status-label ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td>
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEditClient(client)}
                            className="btn btn-sm btn-secondary"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClient(client.id)}
                            className="btn btn-sm btn-danger"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex-1 p-8">
            <div className="empty-state">
              <Users className="empty-state-icon h-12 w-12" />
              <h3 className="empty-state-title">No clients found</h3>
              <p className="empty-state-text">
                {filteredClients.length === 0 && searchQuery ? 
                  'Try adjusting your search criteria' : 
                  'Get started by adding your first client'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Client Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={editingClient ? 'Edit Client' : 'Add New Client'}
        size="lg"
      >
        <ClientForm
          client={editingClient}
          onSubmit={handleFormSubmit}
          onCancel={handleModalClose}
        />
      </Modal>
    </div>
  );
};

export default Clients;
